import Divider from '@mui/material/Divider';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import Globe from '../../assets/globe.svg';
import MapPin from '../../assets/map-pin.svg';
import Users from '../../assets/users.svg';
import Linkedin from '../../assets/linkedinB.svg';
import React, { useEffect, useState } from 'react';
import { IoChevronDown, IoChevronUp, IoClose, IoHomeOutline } from "react-icons/io5";
import { PiUsersThree } from "react-icons/pi";
import activity from '../../assets/activity.svg';
import description from '../../assets/description.svg';
import id from '../../assets/id.svg';
import singleError from '../../assets/singleError.svg';
import notes from '../../assets/notes.svg';
import text from '../../assets/text.svg';
import { useSidebar } from '../../context/SidebarContext';
import { Lead, useLeads } from '../../hooks/useLeads';
import { usePeopleFetch } from '../../hooks/usePeopleFetch';
import categories from '../../pages/HomePage/Leads/assets/categories.svg';
import ActivityCard from './ActivityCard';
import CompanyLogo from './CompanyLogo';
import DecisionMakerCard from './DecisionMakerCard';
import DetailsCard from './DetailsCard';
import Keywords from './Keywords';
import ResearchCard from './ResearchCard';
import apiHandler from '../../services/apiServices';
import like from '../../assets/like.svg';
import dislike from '../../assets/dislike.svg';
import FeedbackPopup from './feedbackpopup';
import { toast } from 'react-toastify';
import FeedbackACK from './feedbackACK';
import { motion } from 'framer-motion';
import NotesCard from './NotesCard';

interface NavOption {
  id: string;
  icon: React.ElementType;
  label: string;
}

const GlobeIcon = () => <img src={Globe} alt="globe" className="w-[16px] h-[16px]" />;
const MapPinIcon = () => <img src={MapPin} alt="map-pin" className="w-[16px] h-[16px]" />;
const UsersIcon = () => <img src={Users} alt="users" className="w-[16px] h-[16px]" />;
const LinkedinIcon = () => <img src={Linkedin} alt="linkedin" className="w-[16px] h-[16px]" />;
const IdIcon = () => <img src={id} alt="id" className="w-[16px] h-[16px]" />;
const DescriptionIcon = () => <img src={description} alt="description" className="w-[16px] h-[16px]" />;
const CategoriesIcon = () => <img src={categories} alt="categories" className="w-[16px] h-[16px]" />;

const navOptions: NavOption[] = [
  {
    id: 'overview',
    icon: IoHomeOutline,
    label: 'Overview'
  },
  {
    id: 'decision-makers',
    icon: PiUsersThree,
    label: 'Decision makers'
  }
];

interface SideDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  lead: Lead | null;
  currentIndex: number;
  totalLeads: number;
  onNavigate: (direction: 'up' | 'down') => void;
  isNavigating: boolean;
  setIsNavigating: (value: boolean) => void;
  allLeads: Lead[];
}

const slideInFromRight = `
  @keyframes slideInFromRight {
    0% {
      opacity: 0;
      transform: translateX(20px);
    }
    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const SideDrawer: React.FC<SideDrawerProps> = ({ isOpen, onClose, lead, currentIndex, totalLeads, onNavigate, isNavigating, setIsNavigating, allLeads }) => {
  const queryClient = useQueryClient();
  const { isSidebarOpen } = useSidebar();
  const [selectedOption, setSelectedOption] = useState('overview');
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedLeftPanel, setSelectedLeftPanel] = useState('activity');
  const [selectedRightPanel, setSelectedRightPanel] = useState('details');
  const [expandedDecisionMaker, setExpandedDecisionMaker] = useState<number[]>(() => {
    if (lead?.company_data?.people && lead.company_data.people.length > 0) {
      return [0]; // Only expand the first decision maker
    }
    return [];
  });
  const [expandedActivity, setExpandedActivity] = useState<number>(0);
  const { mutate: fetchPeople, isPending } = usePeopleFetch();
  const { refetch } = useLeads(20);
  const [selectedFeedback, setSelectedFeedback] = useState<'yes' | 'no' | null>(null);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [showFeedbackSection, setShowFeedbackSection] = useState(true);
  const [showFeedbackAck, setShowFeedbackAck] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  const { data: currentLead } = useQuery({
    queryKey: ['intentLead', lead?._id],
    queryFn: async () => {
      if (!lead?._id) return null;
      const response = await apiHandler.get(`/intent-leads/clubbed/${lead._id}`);
      return response.data;
    },
    enabled: !!lead?._id && isOpen,
    refetchOnWindowFocus: false,
    staleTime: Infinity
  });

  const leadToRender = currentLead || lead;

  useEffect(() => {
    if (lead?.company_data?.people && lead.company_data.people.length > 0) {
      setExpandedDecisionMaker([0]); // Reset to only first decision maker when lead changes
    }
  }, [lead?.company_data?.people]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedOption('overview');
    }
  }, [isOpen]);

  useEffect(() => {
    // Reset states when lead changes
    setSelectedFeedback(null);
    setShowFeedbackSection(false);
    setIsSubmittingFeedback(false);

    // Add delay before showing feedback
    const timer = setTimeout(() => {
      if (lead?.company_data?.user_feedback !== 1 && lead?.company_data?.user_feedback !== -1) {
        setShowFeedbackSection(true);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [lead?._id]); // Changed dependency to lead ID to reset on lead change

  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = slideInFromRight;
    document.head.appendChild(styleSheet);
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  useEffect(() => {
    if (selectedRightPanel === 'decision-maker' && lead && lead.company_data.people_fetched !== true) {
      fetchPeople(lead._id);
    }
  }, [selectedRightPanel, lead, fetchPeople]);

  const handleOptionChange = async (optionId: string) => {
    setSelectedOption(optionId);
  };

  const handleUpClick = async () => {
    const currentIndex = allLeads.findIndex(l => l._id === lead?._id);
    if (currentIndex > 0) {
      const prevLead = allLeads[currentIndex - 1];
      window.history.pushState({}, '', `/accounts/${prevLead._id}`);
      
      try {
        setIsNavigating(true); // Set loading state before navigation
        // Fetch fresh data for the previous lead
        const response = await apiHandler.get(`/intent-leads/clubbed/${prevLead._id}`);
        if (response.data) {
          // Update the lead in the queryClient cache
          queryClient.setQueryData(['intentLead', prevLead._id], response.data);
          onNavigate('up');
        }
      } catch (error) {
        console.error('Error fetching lead data:', error);
        onNavigate('up'); // Still navigate even if fetch fails
      } finally {
        setIsNavigating(false); // Clear loading state after navigation
      }
    }
  };

  const handleDownClick = async () => {
    const currentIndex = allLeads.findIndex(l => l._id === lead?._id);
    if (currentIndex < allLeads.length - 1) {
      const nextLead = allLeads[currentIndex + 1];
      window.history.pushState({}, '', `/accounts/${nextLead._id}`);
      
      try {
        setIsNavigating(true); // Set loading state before navigation
        // Fetch fresh data for the next lead
        const response = await apiHandler.get(`/intent-leads/clubbed/${nextLead._id}`);
        if (response.data) {
          // Update the lead in the queryClient cache
          queryClient.setQueryData(['intentLead', nextLead._id], response.data);
          onNavigate('down');
        }
      } catch (error) {
        console.error('Error fetching lead data:', error);
        onNavigate('down'); // Still navigate even if fetch fails
      } finally {
        setIsNavigating(false); // Clear loading state after navigation
      }
    }
  };

  const handleClose = () => {
    window.history.pushState({}, '', '/accounts');
    onClose();
  };

  const handleRightPanelChange = (panel: string) => {
    setSelectedRightPanel(panel);
  };

  const PeopleSection = ({ lead }: { lead: Lead }) => {
    if (!lead?.company_data.people || lead.company_data.people.length === 0) {
      return (
        <div className="h-full flex flex-col items-center justify-center p-4">
          <div className="flex flex-col items-center justify-center mb-[36px]">
            <img 
              src={singleError} 
              alt="No decision makers" 
              className="w-[168px] h-[42px]"
            />
          </div>
          <p className="text-[14px] text-[#000000] font-medium text-center">
            No decision makers found yet.
          </p>
        </div>
      );
    }

    return (
      <div className="h-full flex flex-col overflow-y-auto scrollbar-hide">
        {lead.company_data.people.map((person, index) => (
          <DecisionMakerCard 
            key={index}
            intentLeadId={lead._id}
            data={{
              profile_pic_url: person.profile_pic_url || undefined,
              first_name: person.first_name,
              last_name: person.last_name,
              designation: person.designation,
              url: person.url,
              email: person.email || undefined,
              is_verified: person.is_verified || false,
              email_status: person.email_status || "unfetched",
              op: person.op
            }}
            isExpanded={expandedDecisionMaker.includes(index)}
            onToggle={() => {
              setExpandedDecisionMaker(prev => {
                if (prev.includes(index)) {
                  return prev.filter(i => i !== index);
                } else {
                  return [...prev, index];
                }
              });
            }}
          />
        ))}
      </div>
    );
  };

  const transformActivities = (lead: Lead) => {
    const currentMonth = new Date().toLocaleString('default', { month: 'long' });
    const currentYear = new Date().getFullYear().toString();

    const activityItems = lead.signals.flatMap(signal => 
      signal.social_media_entities?.map(entity => ({
        type: entity.type as 'post' | 'comment' | 'job',
        title: signal.signal || entity.title || '', 
        description: entity.description,
        url: entity.url,
        date: new Date().toLocaleDateString(),
        companyName: lead.company_data.name,
        signals: [{
          intent_lead_id: signal.intent_lead_id,
          intent_signal_id: signal.intent_signal_id,
          signal: signal.signal,
          social_media_entities: signal.social_media_entities.map(entity => ({
            ...entity,
            title: entity.title || null,
            created_at: entity.created_at || new Date().toISOString()
          })),
          alignment: signal.alignment,
          alignment_reasoning: signal.alignment_reasoning,
          alignment_score: signal.alignment_score
        }]
      })) || []
    );

    return [{
      year: currentYear,
      groups: [{
        month: currentMonth,
        items: activityItems
      }]
    }];
  };

  const handleYesFeedback = async () => {
    try {
      setSelectedFeedback('yes');
      setIsSubmittingFeedback(true);

      await apiHandler.patch(`/intent-leads/${lead?._id}`, {
        user_feedback: 1,
        feedbacks: []
      });

      // Smooth transition out
      setTimeout(() => {
        setShowFeedbackSection(false);
        setShowFeedbackAck(true);
        
        // Hide ACK after 2 seconds
        setTimeout(() => {
          setShowFeedbackAck(false);
        }, 2000);
      }, 300);

    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
      setSelectedFeedback(null);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleNoFeedback = async () => {
    try {
      setSelectedFeedback('no');
      setIsSubmittingFeedback(true);

      await apiHandler.patch(`/intent-leads/${lead?._id}`, {
        user_feedback: -1,
      });

      // Show popup without hiding feedback section yet
      setShowFeedbackPopup(true);

    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
      setSelectedFeedback(null);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleFeedbackPopupClose = async () => {
    try {
      setShowFeedbackPopup(false);
      setShowFeedbackAck(true);
      setShowFeedbackSection(false); // Only hide feedback section when showing ACK
      
      // Hide ACK after 2 seconds
      setTimeout(() => {
        setShowFeedbackAck(false);
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    }
  };

  const handleFeedbackSubmitted = () => {
    setShowFeedbackPopup(false);
    setShowFeedbackAck(true);
    setShowFeedbackSection(false); // Only hide feedback section when showing ACK
    
    // Hide ACK after 2 seconds
    setTimeout(() => {
      setShowFeedbackAck(false);
    }, 2000);
  };

  const handleAddNote = async (note: any) => {
    if (!lead?._id) return false;

    try {
      const existingNotes = lead.notes || [];
      const updatedNotes = [...existingNotes, note];

      // Make PATCH request to update the lead with new notes array
      const response = await apiHandler.patch(`/intent-leads/${lead._id}`, {
        notes: updatedNotes
      });
      
      // Update the lead in the cache with the new notes
      queryClient.setQueryData(['intentLead', lead._id], (oldData: any) => ({
        ...oldData,
        notes: updatedNotes
      }));

      toast.success('Note added successfully');
      return true;
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
      return false;
    }
  };

  if (!isOpen || !lead) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[100]"
        onClick={handleClose}
      />

      {/* Drawer */}
      <div className={`
        fixed 
        inset-[8px]
        ${isSidebarOpen ? 'left-[270px] w-[calc(100%-278px)]' : 'left-[80px] w-[calc(100%-88px)]'}
        bg-[#FFFFFF] 
        shadow-[0_0_15px_rgba(0,0,0,0.1),0_0_6px_rgba(0,0,0,0.05)] 
        z-[101] 
        flex 
        flex-col 
        rounded-[16px] 
        border 
        border-[#EEEFF1] 
        transition-all 
        duration-300
        ${showFeedbackPopup ? 'filter blur-[2px]' : ''}
      `}>
        {/* Close Button */}
        <div className="flex items-center justify-between px-[12px] py-[8px]">
          <div className="flex items-center gap-3">
            <button 
              onClick={handleClose}
              className="hover:bg-gray-100 rounded-[4px] w-[28px] h-[28px] flex items-center justify-center"
            >
              <IoClose className="w-[16px] h-[16px] text-[#7A7A7A]" />
            </button>
            <div className="flex items-center gap-[6px]">
              <button 
                onClick={handleUpClick}
                disabled={currentIndex === 0}
                className={`hover:bg-gray-100 rounded-[8px] w-[28px] h-[28px] flex items-center justify-center border border-[#EEEEEE] shadow-sm ${
                  currentIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <IoChevronUp className="w-[16px] h-[16px] text-[#7A7A7A]" />
              </button>
              <button 
                onClick={handleDownClick}
                disabled={currentIndex === totalLeads - 1}
                className={`hover:bg-gray-100 rounded-[8px] w-[28px] h-[28px] flex items-center justify-center border border-[#EEEEEE] shadow-sm ${
                  currentIndex === totalLeads - 1 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <IoChevronDown className="w-[16px] h-[16px] text-[#7A7A7A]" />
              </button>
            </div>
            <span className="text-[#7A7A7A] text-sm font-[400] ">
              {totalLeads > 0 ? `${currentIndex + 1} of ${totalLeads} in All companies` : 'No companies'}
            </span>
          </div>
        </div>

        <Divider />

        {/* Fixed Header */}
        <div className="flex py-2 pl-2 pr-3 items-center justify-between">
          <div className="flex justify-between items-start p-[4px]">
            <div className="flex flex-col items-start">
              <div className="flex items-center">
                <CompanyLogo
                  name={lead.company_data.name}
                  website={lead.company_data.website}
                  existingLogo={lead.company_data.social_media_company_data?.[0]?.logo}
                  className="w-[28px] h-[28px] rounded-[8px] mr-2"
                />
                <span className="text-[16px]  font-normal text-[#222222]">{lead.company_data.name}</span>
              </div>
            </div>
          </div>

          {/* Feedback */}
          {showFeedbackSection && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="flex items-center justify-between pr-4 gap-4"
            >
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="flex items-center text-xs font-medium text-[#222222]"
              >
                Did you find this lead useful?
              </motion.div>
              <div className="flex items-center gap-2">
                <motion.button 
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ scale: 1.05 }}
                  onClick={handleYesFeedback}
                  disabled={isSubmittingFeedback}
                  className={`flex items-center gap-[2px] p-1 rounded-[16px] transition-all ${
                    selectedFeedback === 'yes' ? 'bg-[#42E56F]' : 'bg-[#E0E2E4]'
                  } ${isSubmittingFeedback ? 'cursor-not-allowed' : ''}`}
                >
                  <img src={like} alt="like" className="w-[14px] h-[14px]" />
                  <span className="text-xs font-normal text-[#000000]">Yes</span>
                </motion.button>
                <motion.button 
                  whileTap={{ scale: 0.95 }}
                  whileHover={{ scale: 1.05 }}
                  onClick={handleNoFeedback}
                  disabled={isSubmittingFeedback}
                  className={`flex items-center gap-[2px] p-1 rounded-[16px] transition-all ${
                    selectedFeedback === 'no' ? 'bg-[#42E56F]' : 'bg-[#E0E2E4]'
                  } ${isSubmittingFeedback ? 'cursor-not-allowed' : ''}`}
                >
                  <img src={dislike} alt="dislike" className="w-[14px] h-[14px]" />
                  <span className="text-xs font-normal text-[#000000]">No</span>
                </motion.button>
              </div>
            </motion.div>
          )}
        </div>

        <Divider />

        {/* Navigation and Content Area */}
        <div className="flex flex-col flex-1 min-h-0">
          {/* Table Header - Navigation Panels */}
          <div className="flex w-full border-b border-[#EEEEEE] flex-shrink-0">
            {/* Left Panel Header - Activity */}
            <div className="w-[58%] border-r border-[#EEEEEE] py-2 px-3 flex gap-2">
              <button 
                onClick={() => setSelectedLeftPanel('activity')}
                className={`flex items-center w-fit justify-center gap-2 px-2 py-1 rounded-[8px] text-[#000000] transition-all
                  ${selectedLeftPanel === 'activity' 
                    ? 'border border-[#EEEEEE] bg-[#FAFAFA]' 
                    : 'hover:bg-[#FAFAFA]'}`}
              >
                <img src={activity} alt="Activity" className="w-[14px] h-[14px]" />
                <span className="text-[14px] font-[400]">Activity</span>
              </button>
              <button 
                onClick={() => setSelectedLeftPanel('research')}
                className={`flex items-center w-fit justify-center gap-2 px-2 py-1 rounded-[8px] text-[#000000] transition-all
                  ${selectedLeftPanel === 'research' 
                    ? 'border border-[#EEEEEE] bg-[#FAFAFA]' 
                    : 'hover:bg-[#FAFAFA]'}`}
              >
                <PiUsersThree className="w-[14px] h-[14px] text-[#7A7A7A]" />
                <span className="text-[14px] font-[400]">Research</span>
              </button>
              {/* <button 
                onClick={() => setSelectedLeftPanel('notes')}
                className={`flex items-center w-fit justify-center gap-2 px-2 py-1 rounded-[8px] text-[#000000] transition-all
                  ${selectedLeftPanel === 'notes' 
                    ? 'border border-[#EEEEEE] bg-[#FAFAFA]' 
                    : 'hover:bg-[#FAFAFA]'}`}
              >
                <img src={notes} alt="Notes" className="w-[14px] h-[14px] text-[#7A7A7A]" />
                <span className="text-[14px] font-[400] ">Notes</span>
              </button> */}
            </div>

            {/* Right Panel Header - Navigation Options */}
            <div className="w-[42%] py-2 px-3 flex gap-2">
              <button 
                onClick={() => setSelectedRightPanel('details')}
                className={`flex items-center w-fit justify-center gap-2 px-2 py-1 rounded-[8px] text-[#000000] transition-all
                  ${selectedRightPanel === 'details' 
                    ? 'border border-[#EEEEEE] bg-[#FAFAFA]' 
                    : 'hover:bg-[#FAFAFA]'}`}
              >
                <PiUsersThree className="w-[14px] h-[14px] text-[#7A7A7A]" />
                <span className="text-[14px] font-[400] ">Details</span>
              </button>

              <button 
                onClick={() => handleRightPanelChange('decision-maker')}
                className={`flex items-center w-fit justify-center gap-2 px-2 py-1 rounded-[8px] text-[#000000] transition-all
                  ${selectedRightPanel === 'decision-maker' 
                    ? 'border border-[#EEEEEE] bg-[#FAFAFA]' 
                    : 'hover:bg-[#FAFAFA]'}`}
              >
                <PiUsersThree className="w-[14px] h-[14px] text-[#7A7A7A]" />
                <span className="text-[14px] font-[400] ">Decision maker</span>
              </button>

              <button 
                onClick={() => setSelectedRightPanel('keywords')}
                className={`flex items-center w-fit justify-center gap-2 px-2 py-1 rounded-[8px] text-[#000000] transition-all
                  ${selectedRightPanel === 'keywords' 
                    ? 'border border-[#EEEEEE] bg-[#FAFAFA]' 
                    : 'hover:bg-[#FAFAFA]'}`}
              >
                <img src={text} alt="Keywords" className="w-[14px] h-[14px] text-[#7A7A7A]" />
                <span className="text-[14px] font-[400] ">Keywords</span>
              </button>
            </div>
          </div>

          {/* Table Body - Content Area */}
          <div className="flex flex-1 min-h-0 overflow-hidden">
            {/* Left Panel Content - Activity */}
            <div className="w-[58%] border-r border-[#EEEEEE] overflow-y-auto scrollbar-hide">
              <div className="h-full p-4">
                <div className="h-full">
                  {selectedLeftPanel === 'research' && (
                    <ResearchCard 
                      items={lead.company_data.research_resources || []}
                      className="h-full"
                    />
                  )}
                  {selectedLeftPanel === 'activity' && (
                    <ActivityCard
                      activities={transformActivities(lead)}
                      className="h-full"
                      expandedActivity={expandedActivity}
                      onActivityExpand={(index: number) => setExpandedActivity(index)}
                    />
                  )}
                  {selectedLeftPanel === 'notes' && (
                    <NotesCard
                      notes={lead.notes || []}
                      className="h-full"
                      onAddNote={handleAddNote}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Right Panel Content */}
            <div className="w-[42%] overflow-hidden flex flex-col">
              <div className="flex-1 overflow-y-auto scrollbar-hide">
                {selectedRightPanel === 'details' && (
                  <DetailsCard
                    title="Company details"
                    details={[
                      {
                        icon: GlobeIcon,
                        label: "Website",
                        value: lead.company_data.company_website_data?.domain
                      },
                      {
                        icon: IdIcon,
                        label: "Name",
                        value: lead.company_data.name
                      },
                      {
                        icon: CategoriesIcon,
                        label: "Categories",
                        value: lead.company_data.social_media_company_data?.[0]?.industries?.split(',').map(industry => industry.trim()) || []
                      },
                      {
                        icon: UsersIcon,
                        label: "Employee count",
                        value: `${lead.company_data.social_media_company_data?.[0]?.employees_count || 'N/A'} employees`
                      },
                      {
                        icon: MapPinIcon,
                        label: "Location",
                        value: lead.company_data.social_media_company_data?.[0]?.location || 'N/A'
                      },
                      {
                        icon: DescriptionIcon,
                        label: "Description",
                        value: lead.description || lead.company_data.social_media_company_data?.[0]?.description || 'N/A'
                      },
                      {
                        icon: LinkedinIcon,
                        label: "LinkedIn",
                        value: lead.company_data.social_media_company_data?.[0]?.url ? (
                          <a 
                            href={lead.company_data.social_media_company_data[0].url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-[#3F64B6] hover:underline"
                          >
                            View Profile
                          </a>
                        ) : 'N/A'
                      }
                    ]}
                    className="h-full"
                  />
                )}
                {selectedRightPanel === 'decision-maker' && (
                  <div className="h-full overflow-y-auto">
                    <PeopleSection lead={leadToRender} />
                  </div>
                )}
                {selectedRightPanel === 'keywords' && (
                  <Keywords
                    keywords={lead.keywords || []}
                    onAdd={(keyword: string) => {
                      const updatedLead = {
                        ...lead,
                        keywords: [...(lead.keywords || []), keyword]
                      };
                      queryClient.setQueryData(['intentLead', lead._id], updatedLead);
                    }}
                    className="h-full"
                    signalId={lead._id}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {isNavigating && (
          <div 
            className="absolute inset-0 bg-[#EEEEEE]/45 backdrop-blur-[2px] flex items-center justify-center z-50 transition-opacity duration-300"
            style={{
              animation: 'fadeIn 0.3s ease-in-out'
            }}
          >
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#222222] border-t-transparent"></div>
              <span className="text-sm text-[#000000]">Loading...</span>
            </div>
          </div>
        )}
      </div>

      <FeedbackPopup 
        isOpen={showFeedbackPopup}
        onClose={handleFeedbackPopupClose}
        leadId={lead?._id || ''}
        onSubmitted={handleFeedbackSubmitted}
      />

      <FeedbackACK 
        isOpen={showFeedbackAck}
        onClose={() => setShowFeedbackAck(false)}
      />
    </>
  );
};

export default SideDrawer;